import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box,
  Grid,
  GridItem,
  Heading,
  Text,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  useColorModeValue,
  Flex,
  Icon,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Badge,
  Button,
  IconButton,
  HStack,
  VStack,
  Tooltip,
  Progress,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Select,
  Switch,
  FormControl,
  FormLabel,
  Divider,
  useToast,
  Tag,
  TagLabel,
  TagLeftIcon,
  Skeleton,
  SkeletonText,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  useDisclosure,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Collapse,
  Center,
} from '@chakra-ui/react';
import {
  MdTimer,
  MdCheckCircle,
  MdWarning,
  MdError,
  MdRefresh,
  MdDownload,
  MdTrendingUp,
  MdTrendingDown,
  MdSearch,
  MdFilterList,
  MdExpandMore,
  MdExpandLess,
  MdNotifications,
  MdNotificationsOff,
  MdInfo,
  MdSchedule,
  MdPriorityHigh,
  MdSpeed,
  MdAssignment,
  MdPerson,
  MdBusiness,
  MdAnalytics,
  MdFileDownload,
  MdOpenInNew,
} from 'react-icons/md';
import { ChevronDownIcon, WarningIcon } from '@chakra-ui/icons';
import ReactApexChart from 'react-apexcharts';
import { authenticatedFetch } from '../../services/api';

// ============================================================================
// CONSTANTS
// ============================================================================
const SLA_WINDOWS = {
  critical: { hours: 4,  label: '4 hrs',  color: 'red'    },
  high:     { hours: 8,  label: '8 hrs',  color: 'orange' },
  medium:   { hours: 24, label: '24 hrs', color: 'yellow' },
  low:      { hours: 72, label: '72 hrs', color: 'green'  },
};

const STATUS_CONFIG = {
  met:      { color: 'green',  icon: MdCheckCircle, label: 'Met'      },
  at_risk:  { color: 'orange', icon: MdWarning,     label: 'At Risk'  },
  breached: { color: 'red',    icon: MdError,       label: 'Breached' },
  pending:  { color: 'gray',   icon: MdSchedule,    label: 'Pending'  },
};

const LOG_PREFIX = '[SlaComplianceDashboard]';

// ============================================================================
// HELPER — format minutes into human-readable duration
// ============================================================================
const formatDuration = (minutes) => {
  if (!minutes && minutes !== 0) return 'N/A';
  if (minutes < 60) return `${minutes}m`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
};

// ============================================================================
// HELPER — derive a RAG status colour for compliance rate
// ============================================================================
const getComplianceColor = (rate) => {
  if (rate >= 95) return 'green';
  if (rate >= 85) return 'yellow';
  if (rate >= 70) return 'orange';
  return 'red';
};

// ============================================================================
// SUB-COMPONENT — Skeleton loader cards
// ============================================================================
const StatCardSkeleton = () => (
  <Box p={5} borderRadius="xl" borderWidth="1px">
    <Skeleton height="20px" mb={3} w="60%" />
    <Skeleton height="40px" mb={2} w="40%" />
    <SkeletonText noOfLines={1} w="80%" />
  </Box>
);

// ============================================================================
// SUB-COMPONENT — KPI Stat Card
// ============================================================================
const SlaStatCard = ({ label, value, helpText, icon, iconBg, iconColor, trend, trendLabel, suffix = '', isLoading }) => {
  // FIX: Hooks must be at the very top, before any conditional logic or early returns
  const cardBg    = useColorModeValue('white', 'gray.800');
  const borderCol = useColorModeValue('gray.200', 'gray.700');
  const labelCol  = useColorModeValue('gray.600', 'gray.400');

  if (isLoading) return <StatCardSkeleton />;

  return (
    <Stat
      p={5}
      bg={cardBg}
      borderRadius="xl"
      borderWidth="1px"
      borderColor={borderCol}
      boxShadow="sm"
      transition="all 0.2s"
      _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
    >
      <Flex align="center" mb={3}>
        <Box p={2} bg={iconBg} borderRadius="lg" mr={3}>
          <Icon as={icon} color={iconColor} boxSize={5} />
        </Box>
        <StatLabel fontWeight="600" fontSize="sm" color={labelCol}>{label}</StatLabel>
      </Flex>
      <StatNumber fontSize="3xl" fontWeight="800" lineHeight="1">
        {value}{suffix}
      </StatNumber>
      <StatHelpText mt={2} mb={0} fontSize="xs">
        {trend !== undefined && (
          <StatArrow type={trend >= 0 ? 'increase' : 'decrease'} />
        )}
        {helpText}
        {trendLabel && (
          <Text as="span" ml={1} color={trend >= 0 ? 'green.500' : 'red.500'} fontWeight="600">
            {trendLabel}
          </Text>
        )}
      </StatHelpText>
    </Stat>
  );
};

// ============================================================================
// SUB-COMPONENT — Priority progress row
// ============================================================================
const PriorityRow = ({ priority, met, total, isLoading }) => {
  // FIX: Hooks must be at the top
  const textCol = useColorModeValue('gray.700', 'gray.200');
  const mutedCol = useColorModeValue('gray.500', 'gray.400');
  const progressBg = useColorModeValue('gray.100', 'gray.700');

  const cfg    = SLA_WINDOWS[priority] || {};
  const pct    = total > 0 ? Math.round((met / total) * 100) : 0;
  const color  = getComplianceColor(pct);

  if (isLoading) return <Skeleton height="40px" borderRadius="md" />;

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={1}>
        <HStack spacing={2}>
          <Badge colorScheme={cfg.color || 'gray'} fontSize="xs" textTransform="capitalize">
            {priority}
          </Badge>
          <Text fontSize="sm" fontWeight="600" color={textCol}>{cfg.label} SLA</Text>
        </HStack>
        <HStack spacing={2}>
          <Text fontSize="sm" fontWeight="700" color={`${color}.500`}>{pct}%</Text>
          <Text fontSize="xs" color={mutedCol}>({met}/{total})</Text>
        </HStack>
      </Flex>
      <Progress
        value={pct}
        colorScheme={color}
        size="sm"
        borderRadius="full"
        bg={progressBg}
      />
    </Box>
  );
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
const SlaComplianceDashboard = ({ dateRange }) => {
  // --------------------------------------------------------------------------
  // STATE
  // --------------------------------------------------------------------------
  const [metrics, setMetrics]             = useState(null);
  const [breachedTickets, setBreachedTickets] = useState([]);
  const [atRiskTickets, setAtRiskTickets]  = useState([]);
  const [trendData, setTrendData]          = useState([]);
  const [zoneBreakdown, setZoneBreakdown]  = useState([]);
  const [techBreakdown, setTechBreakdown]  = useState([]);
  const [isLoading, setIsLoading]          = useState(true);
  const [isRefreshing, setIsRefreshing]    = useState(false);
  const [error, setError]                  = useState(null);
  const [lastFetched, setLastFetched]      = useState(null);
  const [autoRefresh, setAutoRefresh]      = useState(false);
  const [searchQuery, setSearchQuery]      = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [sortField, setSortField]          = useState('time_remaining');
  const [sortDir, setSortDir]              = useState('asc');
  const [activeDetailTab, setActiveDetailTab] = useState(0);
  const [showFilters, setShowFilters]      = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);

  const autoRefreshRef = useRef(null);
  const toast = useToast();
  const { isOpen: isTicketOpen, onOpen: onTicketOpen, onClose: onTicketClose } = useDisclosure();

  // --------------------------------------------------------------------------
  // THEME
  // --------------------------------------------------------------------------
  const cardBg        = useColorModeValue('white', 'gray.800');
  const borderColor   = useColorModeValue('gray.200', 'gray.700');
  const bgColor       = useColorModeValue('gray.50', 'gray.900');
  const headingColor  = useColorModeValue('gray.800', 'white');
  const mutedColor    = useColorModeValue('gray.500', 'gray.400');
  const tableHeadBg   = useColorModeValue('gray.50', 'gray.700');
  const tableRowHover = useColorModeValue('cyan.50', 'gray.700');
  const chartFg       = useColorModeValue('#2D3748', '#E2E8F0');
  const chartGrid     = useColorModeValue('#E2E8F0', '#4A5568');
  const autoRefreshBg = useColorModeValue('cyan.50', 'cyan.900');

  // --------------------------------------------------------------------------
  // DATA FETCHING
  // --------------------------------------------------------------------------
  const fetchAllSlaData = useCallback(async (silent = false) => {
    if (!silent) setIsLoading(true);
    else setIsRefreshing(true);
    setError(null);

    const params = `start=${dateRange.start}&end=${dateRange.end}`;

    console.groupCollapsed(`${LOG_PREFIX} Fetching SLA data`);
    console.log('Date range:', dateRange);

    try {
      // Fire all requests in parallel for performance
      const [metricsRes, breachedRes, atRiskRes, trendRes, zoneRes, techRes] = await Promise.allSettled([
        authenticatedFetch(`/analytics/sla-metrics?${params}`),
        authenticatedFetch(`/analytics/sla-breached?${params}&limit=50`),
        authenticatedFetch(`/analytics/sla-at-risk?${params}&limit=50`),
        authenticatedFetch(`/analytics/sla-trend?${params}`),
        authenticatedFetch(`/analytics/sla-by-zone?${params}`),
        authenticatedFetch(`/analytics/sla-by-technician?${params}`),
      ]);

      // --- Metrics (core KPIs) ---
      if (metricsRes.status === 'fulfilled') {
        const data = metricsRes.value?.data || metricsRes.value;
        setMetrics({
          complianceRate:     data.complianceRate      ?? 0,
          currentlyAtRisk:    data.currentlyAtRisk     ?? 0,
          currentlyBreached:  data.currentlyBreached   ?? 0,
          totalMonitored:     data.totalMonitored      ?? 0,
          avgResolutionTime:  data.avgResolutionTime   ?? 0,   // minutes
          avgResponseTime:    data.avgResponseTime     ?? 0,   // minutes
          complianceTrend:    data.complianceTrend     ?? 0,   // delta % vs previous period
          byPriority: {
            critical: { met: data.critical_met ?? 0, total: data.critical_total ?? 0 },
            high:     { met: data.high_met     ?? 0, total: data.high_total     ?? 0 },
            medium:   { met: data.medium_met   ?? 0, total: data.medium_total   ?? 0 },
            low:      { met: data.low_met      ?? 0, total: data.low_total      ?? 0 },
          },
        });
        console.log(`${LOG_PREFIX} Metrics loaded`, data);
      } else {
        console.warn(`${LOG_PREFIX} Metrics fetch failed`, metricsRes.reason);
      }

      // --- Breached tickets ---
      if (breachedRes.status === 'fulfilled') {
        const rows = breachedRes.value?.data ?? breachedRes.value ?? [];
        setBreachedTickets(Array.isArray(rows) ? rows : []);
        console.log(`${LOG_PREFIX} Breached tickets: ${rows.length}`);
      }

      // --- At-risk tickets ---
      if (atRiskRes.status === 'fulfilled') {
        const rows = atRiskRes.value?.data ?? atRiskRes.value ?? [];
        setAtRiskTickets(Array.isArray(rows) ? rows : []);
        console.log(`${LOG_PREFIX} At-risk tickets: ${rows.length}`);
      }

      // --- Trend series ---
      if (trendRes.status === 'fulfilled') {
        const rows = trendRes.value?.data ?? trendRes.value ?? [];
        setTrendData(Array.isArray(rows) ? rows : []);
        console.log(`${LOG_PREFIX} Trend points: ${rows.length}`);
      }

      // --- Zone breakdown ---
      if (zoneRes.status === 'fulfilled') {
        const rows = zoneRes.value?.data ?? zoneRes.value ?? [];
        setZoneBreakdown(Array.isArray(rows) ? rows : []);
      }

      // --- Technician breakdown ---
      if (techRes.status === 'fulfilled') {
        const rows = techRes.value?.data ?? techRes.value ?? [];
        setTechBreakdown(Array.isArray(rows) ? rows : []);
      }

      setLastFetched(new Date());

      if (silent) {
        toast({
          title: 'SLA data refreshed',
          status: 'success',
          duration: 1500,
          isClosable: true,
          position: 'bottom-right',
        });
      }
    } catch (err) {
      console.error(`${LOG_PREFIX} Fetch error:`, err);
      setError(err.message || 'Failed to load SLA compliance data.');
      if (!silent) {
        toast({
          title: 'Failed to load SLA data',
          description: err.message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
      console.groupEnd();
    }
  }, [dateRange, toast]);

  // Initial load + date-range change
  useEffect(() => {
    fetchAllSlaData(false);
  }, [fetchAllSlaData]);

  // Auto-refresh timer
  useEffect(() => {
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(() => fetchAllSlaData(true), 60_000);
      console.log(`${LOG_PREFIX} Auto-refresh enabled (60s interval)`);
    } else {
      clearInterval(autoRefreshRef.current);
    }
    return () => clearInterval(autoRefreshRef.current);
  }, [autoRefresh, fetchAllSlaData]);

  // --------------------------------------------------------------------------
  // DERIVED DATA
  // --------------------------------------------------------------------------
  const filteredAtRisk = useMemo(() => {
    let rows = [...atRiskTickets];
    if (priorityFilter !== 'all') rows = rows.filter(r => r.priority === priorityFilter);
    if (searchQuery)              rows = rows.filter(r =>
      r.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.work_order_id?.toString().includes(searchQuery) ||
      r.assigned_to?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    rows.sort((a, b) => {
      const av = a[sortField] ?? 0;
      const bv = b[sortField] ?? 0;
      return sortDir === 'asc' ? (av > bv ? 1 : -1) : (av < bv ? 1 : -1);
    });
    return rows;
  }, [atRiskTickets, priorityFilter, searchQuery, sortField, sortDir]);

  const filteredBreached = useMemo(() => {
    let rows = [...breachedTickets];
    if (priorityFilter !== 'all') rows = rows.filter(r => r.priority === priorityFilter);
    if (searchQuery)              rows = rows.filter(r =>
      r.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.work_order_id?.toString().includes(searchQuery) ||
      r.assigned_to?.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return rows;
  }, [breachedTickets, priorityFilter, searchQuery]);

  // --------------------------------------------------------------------------
  // CHART CONFIGS
  // --------------------------------------------------------------------------
  // Trend area chart
  const trendChartOptions = useMemo(() => ({
    chart: { type: 'area', toolbar: { show: false }, background: 'transparent', fontFamily: 'inherit', animations: { enabled: true, speed: 600 } },
    colors: ['#0BC5EA', '#F56565'],
    stroke: { curve: 'smooth', width: [3, 2] },
    fill: {
      type: 'gradient',
      gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.05, stops: [0, 90, 100] },
    },
    xaxis: {
      categories: trendData.map(d => d.label || d.date || d.week),
      labels: { style: { colors: chartFg, fontSize: '11px' } },
    },
    yaxis: {
      min: 0, max: 100,
      labels: { formatter: (v) => `${v}%`, style: { colors: chartFg, fontSize: '11px' } },
    },
    tooltip: { theme: 'dark', y: { formatter: (v) => `${v.toFixed(1)}%` } },
    dataLabels: { enabled: false },
    legend: { position: 'top', labels: { colors: chartFg } },
    grid: { borderColor: chartGrid, strokeDashArray: 4 },
    markers: { size: 4, hover: { size: 6 } },
  }), [trendData, chartFg, chartGrid]);

  const trendChartSeries = useMemo(() => {
    if (trendData.length > 0) {
      return [
        { name: 'Compliance Rate (%)', data: trendData.map(d => parseFloat((d.complianceRate ?? d.compliance_rate ?? 0).toFixed(1))) },
        { name: 'Breach Rate (%)',     data: trendData.map(d => parseFloat((d.breachRate ?? d.breach_rate ?? 0).toFixed(1)))      },
      ];
    }
    // Fallback placeholder while loading
    return [
      { name: 'Compliance Rate (%)', data: [92, 94, 89, metrics?.complianceRate ?? 95] },
      { name: 'Breach Rate (%)',     data: [8,   6, 11, 100 - (metrics?.complianceRate ?? 95)] },
    ];
  }, [trendData, metrics]);

  // Zone donut chart
  const zoneChartOptions = useMemo(() => ({
    chart: { type: 'donut', background: 'transparent', fontFamily: 'inherit' },
    labels: zoneBreakdown.map(z => z.zone_name || z.zone),
    colors: ['#0BC5EA', '#48BB78', '#F6AD55', '#FC8181', '#9F7AEA', '#68D391'],
    legend: { position: 'bottom', labels: { colors: chartFg } },
    tooltip: { theme: 'dark', y: { formatter: (v) => `${v.toFixed(1)}%` } },
    dataLabels: { enabled: true, style: { colors: [chartFg] }, dropShadow: { enabled: false } },
    plotOptions: { pie: { donut: { size: '65%', labels: { show: true, total: { show: true, label: 'Avg', formatter: () => `${metrics?.complianceRate ?? 0}%` } } } } },
  }), [zoneBreakdown, chartFg, metrics]);

  const zoneChartSeries = useMemo(() =>
    zoneBreakdown.map(z => parseFloat((z.compliance_rate ?? z.complianceRate ?? 0).toFixed(1))),
    [zoneBreakdown]
  );

  // Priority donut chart
  const priorityChartOptions = useMemo(() => ({
    chart: { type: 'radialBar', background: 'transparent', fontFamily: 'inherit' },
    colors: ['#FC8181', '#F6AD55', '#F6E05E', '#68D391'],
    plotOptions: {
      radialBar: {
        offsetY: 0,
        startAngle: -135, endAngle: 135,
        hollow: { margin: 5, size: '40%' },
        dataLabels: { name: { fontSize: '11px', color: chartFg }, value: { fontSize: '16px', color: chartFg, formatter: (v) => `${v}%` } },
        track: { background: chartGrid },
      },
    },
    labels: ['Critical', 'High', 'Medium', 'Low'],
    legend: { show: true, position: 'bottom', labels: { colors: chartFg } },
  }), [chartFg, chartGrid]);

  const priorityChartSeries = useMemo(() => {
    const p = metrics?.byPriority;
    if (!p) return [0, 0, 0, 0];
    const calc = (met, total) => total > 0 ? Math.round((met / total) * 100) : 0;
    return [
      calc(p.critical.met, p.critical.total),
      calc(p.high.met,     p.high.total),
      calc(p.medium.met,   p.medium.total),
      calc(p.low.met,      p.low.total),
    ];
  }, [metrics]);

  // --------------------------------------------------------------------------
  // CSV EXPORT
  // --------------------------------------------------------------------------
  const handleExport = useCallback(() => {
    const rows = activeDetailTab === 0 ? filteredAtRisk : filteredBreached;
    if (rows.length === 0) {
      toast({ title: 'No data to export', status: 'warning', duration: 2000 });
      return;
    }
    const headers = ['ID', 'Title', 'Priority', 'Assigned To', 'Zone', 'Time Remaining', 'SLA Window', 'Status'];
    const csv = [
      headers.join(','),
      ...rows.map(r => [
        r.work_order_id, `"${r.title}"`, r.priority, `"${r.assigned_to || 'Unassigned'}"`,
        `"${r.zone_name || ''}"`, formatDuration(r.time_remaining_minutes),
        SLA_WINDOWS[r.priority]?.label || '', r.sla_status || '',
      ].join(',')),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `sla-${activeDetailTab === 0 ? 'at-risk' : 'breached'}-${dateRange.start}-${dateRange.end}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast({ title: 'Export complete', status: 'success', duration: 2000 });
    console.log(`${LOG_PREFIX} CSV exported`, { rows: rows.length });
  }, [filteredAtRisk, filteredBreached, activeDetailTab, dateRange, toast]);

  // --------------------------------------------------------------------------
  // COLUMN SORT TOGGLE
  // --------------------------------------------------------------------------
  const toggleSort = useCallback((field) => {
    if (sortField === field) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDir('asc'); }
  }, [sortField]);

  // --------------------------------------------------------------------------
  // TICKET DETAIL MODAL OPEN
  // --------------------------------------------------------------------------
  const openTicketDetail = useCallback((ticket) => {
    setSelectedTicket(ticket);
    onTicketOpen();
    console.log(`${LOG_PREFIX} Ticket detail opened`, ticket.work_order_id);
  }, [onTicketOpen]);

  // --------------------------------------------------------------------------
  // LOADING STATE — full skeleton
  // --------------------------------------------------------------------------
  if (isLoading) {
    return (
      <Box>
        <Flex align="center" justify="space-between" mb={6}>
          <Box>
            <Skeleton height="28px" w="280px" mb={2} />
            <Skeleton height="16px" w="420px" />
          </Box>
          <Skeleton height="36px" w="100px" borderRadius="lg" />
        </Flex>
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={5} mb={8}>
          {[0, 1, 2, 3].map(i => <StatCardSkeleton key={i} />)}
        </SimpleGrid>
        <Grid templateColumns="repeat(12, 1fr)" gap={6}>
          <GridItem colSpan={{ base: 12, lg: 8 }}>
            <Skeleton height="380px" borderRadius="xl" />
          </GridItem>
          <GridItem colSpan={{ base: 12, lg: 4 }}>
            <Skeleton height="380px" borderRadius="xl" />
          </GridItem>
        </Grid>
      </Box>
    );
  }

  // --------------------------------------------------------------------------
  // ERROR STATE
  // --------------------------------------------------------------------------
  if (error && !metrics) {
    return (
      <Alert status="error" borderRadius="xl" variant="left-accent">
        <AlertIcon />
        <Box>
          <AlertTitle>Failed to load SLA compliance data</AlertTitle>
          <AlertDescription fontSize="sm">{error}</AlertDescription>
        </Box>
        <Button ml="auto" size="sm" colorScheme="red" variant="outline" leftIcon={<Icon as={MdRefresh} />} onClick={() => fetchAllSlaData(false)}>
          Retry
        </Button>
      </Alert>
    );
  }

  const complianceColor = getComplianceColor(metrics?.complianceRate ?? 0);

  // --------------------------------------------------------------------------
  // MAIN RENDER
  // --------------------------------------------------------------------------
  return (
    <Box>
      {/* ====== SECTION HEADER ====== */}
      <Flex justify="space-between" align="flex-start" mb={6} flexWrap="wrap" gap={4}>
        <Box>
          <HStack spacing={3} mb={1}>
            <Box p={2} bg="cyan.50" borderRadius="lg">
              <Icon as={MdTimer} color="cyan.500" boxSize={6} />
            </Box>
            <Heading size="md" color={headingColor}>SLA Compliance Overview</Heading>
            {metrics?.complianceTrend !== undefined && (
              <Badge
                colorScheme={metrics.complianceTrend >= 0 ? 'green' : 'red'}
                fontSize="xs"
                borderRadius="full"
                px={2}
              >
                <HStack spacing={1}>
                  <Icon as={metrics.complianceTrend >= 0 ? MdTrendingUp : MdTrendingDown} boxSize={3} />
                  <Text>{Math.abs(metrics.complianceTrend).toFixed(1)}% vs prev period</Text>
                </HStack>
              </Badge>
            )}
          </HStack>
          <Text color={mutedColor} fontSize="sm">
            Real-time monitoring of regulatory resolution times across all active work orders.
          </Text>
          {lastFetched && (
            <Text color={mutedColor} fontSize="xs" mt={1}>
              Last updated: {lastFetched.toLocaleTimeString()}
            </Text>
          )}
        </Box>

        <HStack spacing={2} flexWrap="wrap">
          {/* Auto-refresh toggle */}
          <Tooltip label={autoRefresh ? 'Disable auto-refresh' : 'Enable auto-refresh (60s)'} hasArrow>
            <Box
              px={3} py={1.5} borderRadius="lg" borderWidth="1px"
              borderColor={autoRefresh ? 'cyan.400' : borderColor}
              bg={autoRefresh ? autoRefreshBg : 'transparent'}
            >
              <FormControl display="flex" alignItems="center" w="auto">
                <Icon as={autoRefresh ? MdNotifications : MdNotificationsOff} mr={2} color={autoRefresh ? 'cyan.500' : mutedColor} boxSize={4} />
                <FormLabel mb={0} fontSize="xs" fontWeight="600" mr={2} cursor="pointer">Auto</FormLabel>
                <Switch size="sm" isChecked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} colorScheme="cyan" />
              </FormControl>
            </Box>
          </Tooltip>

          {/* Manual refresh */}
          <Tooltip label="Refresh data" hasArrow>
            <IconButton
              icon={<Icon as={MdRefresh} />}
              size="sm"
              variant="outline"
              onClick={() => fetchAllSlaData(true)}
              isLoading={isRefreshing}
              aria-label="Refresh"
              borderRadius="lg"
            />
          </Tooltip>

          {/* Export */}
          <Tooltip label="Export CSV" hasArrow>
            <IconButton
              icon={<Icon as={MdFileDownload} />}
              size="sm"
              variant="outline"
              onClick={handleExport}
              aria-label="Export CSV"
              borderRadius="lg"
            />
          </Tooltip>
        </HStack>
      </Flex>

      {/* ====== SLA BREACH ALERT BANNER ====== */}
      {(metrics?.currentlyBreached ?? 0) > 0 && (
        <Alert status="error" borderRadius="xl" mb={6} variant="left-accent">
          <AlertIcon />
          <Box flex="1">
            <AlertTitle fontSize="sm">Active SLA Breaches Detected</AlertTitle>
            <AlertDescription fontSize="xs">
              {metrics.currentlyBreached} work order{metrics.currentlyBreached !== 1 ? 's have' : ' has'} exceeded their
              resolution window. Immediate action is required.
            </AlertDescription>
          </Box>
          <Button
            size="xs"
            colorScheme="red"
            variant="solid"
            ml={4}
            onClick={() => { setActiveDetailTab(1); document.getElementById('sla-detail-table')?.scrollIntoView({ behavior: 'smooth' }); }}
          >
            View Breached
          </Button>
        </Alert>
      )}

      {/* ====== KPI STAT CARDS ====== */}
      <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={5} mb={8}>
        <SlaStatCard
          label="Overall Compliance"
          value={metrics?.complianceRate ?? 0}
          suffix="%"
          helpText="Target: > 90%"
          icon={MdCheckCircle}
          iconBg={`${complianceColor}.50`}
          iconColor={`${complianceColor}.500`}
          trend={metrics?.complianceTrend}
          trendLabel={metrics?.complianceTrend !== undefined ? `${metrics.complianceTrend >= 0 ? '+' : ''}${metrics.complianceTrend.toFixed(1)}%` : undefined}
        />
        <SlaStatCard
          label="Tickets At Risk"
          value={metrics?.currentlyAtRisk ?? 0}
          helpText="Approaching resolution deadline"
          icon={MdWarning}
          iconBg="orange.50"
          iconColor="orange.500"
        />
        <SlaStatCard
          label="Breached SLAs"
          value={metrics?.currentlyBreached ?? 0}
          helpText="Missed resolution windows"
          icon={MdError}
          iconBg="red.50"
          iconColor="red.500"
        />
        <SlaStatCard
          label="Avg Resolution Time"
          value={formatDuration(metrics?.avgResolutionTime)}
          helpText={`Avg response: ${formatDuration(metrics?.avgResponseTime)}`}
          icon={MdSpeed}
          iconBg="cyan.50"
          iconColor="cyan.500"
        />
      </SimpleGrid>

      {/* ====== CHARTS ROW ====== */}
      <Grid templateColumns="repeat(12, 1fr)" gap={6} mb={8}>
        {/* Trend Chart */}
        <GridItem colSpan={{ base: 12, lg: 8 }}>
          <Box p={5} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} boxShadow="sm">
            <Flex justify="space-between" align="center" mb={4}>
              <Heading size="sm" color={headingColor}>Compliance Trend</Heading>
              <Badge colorScheme={complianceColor} fontSize="xs" px={2} py={1} borderRadius="full">
                {metrics?.complianceRate ?? 0}% current
              </Badge>
            </Flex>
            <ReactApexChart
              options={trendChartOptions}
              series={trendChartSeries}
              type="area"
              height={300}
            />
          </Box>
        </GridItem>

        {/* Priority Radial Chart */}
        <GridItem colSpan={{ base: 12, lg: 4 }}>
          <Box p={5} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} boxShadow="sm" h="100%">
            <Heading size="sm" mb={2} color={headingColor}>By Priority</Heading>
            <Text fontSize="xs" color={mutedColor} mb={3}>Compliance rate per SLA tier</Text>
            <ReactApexChart
              options={priorityChartOptions}
              series={priorityChartSeries}
              type="radialBar"
              height={260}
            />
          </Box>
        </GridItem>
      </Grid>

      {/* ====== PRIORITY BREAKDOWN + ZONE CHART ====== */}
      <Grid templateColumns="repeat(12, 1fr)" gap={6} mb={8}>
        {/* Priority Progress Breakdown */}
        <GridItem colSpan={{ base: 12, lg: 6 }}>
          <Box p={5} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} boxShadow="sm">
            <Flex justify="space-between" align="center" mb={5}>
              <Heading size="sm" color={headingColor}>Priority Matrix</Heading>
              <Tooltip label="SLA windows define the max time to resolve per priority tier." hasArrow>
                <Icon as={MdInfo} color={mutedColor} boxSize={4} cursor="help" />
              </Tooltip>
            </Flex>
            <Alert status="info" borderRadius="lg" mb={5} py={2} px={3} fontSize="xs">
              <AlertIcon boxSize={3.5} />
              Critical tickets mandate a 4-hour resolution window per MOW-WSC SLA policy.
            </Alert>
            <VStack spacing={4} align="stretch">
              {Object.entries(metrics?.byPriority ?? {}).map(([prio, { met, total }]) => (
                <PriorityRow key={prio} priority={prio} met={met} total={total} />
              ))}
            </VStack>
          </Box>
        </GridItem>

        {/* Zone Compliance Donut */}
        <GridItem colSpan={{ base: 12, lg: 6 }}>
          <Box p={5} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} boxShadow="sm">
            <Heading size="sm" mb={1} color={headingColor}>Compliance by Zone</Heading>
            <Text fontSize="xs" color={mutedColor} mb={3}>SLA compliance rate per service zone</Text>
            {zoneBreakdown.length > 0 ? (
              <ReactApexChart
                options={zoneChartOptions}
                series={zoneChartSeries}
                type="donut"
                height={280}
              />
            ) : (
              <Center h="240px">
                <Text fontSize="sm" color={mutedColor}>No zone data available for this period.</Text>
              </Center>
            )}
          </Box>
        </GridItem>
      </Grid>

      {/* ====== TECHNICIAN LEADERBOARD ====== */}
      {techBreakdown.length > 0 && (
        <Box p={5} bg={cardBg} borderRadius="xl" borderWidth="1px" borderColor={borderColor} boxShadow="sm" mb={8}>
          <Flex justify="space-between" align="center" mb={4}>
            <Heading size="sm" color={headingColor}>Technician SLA Performance</Heading>
            <Badge colorScheme="cyan" fontSize="xs">Top {Math.min(techBreakdown.length, 10)}</Badge>
          </Flex>
          <TableContainer>
            <Table size="sm" variant="simple">
              <Thead bg={tableHeadBg}>
                <Tr>
                  <Th>Technician</Th>
                  <Th isNumeric>Assigned</Th>
                  <Th isNumeric>Met SLA</Th>
                  <Th isNumeric>Breached</Th>
                  <Th isNumeric>Compliance</Th>
                  <Th isNumeric>Avg Resolution</Th>
                </Tr>
              </Thead>
              <Tbody>
                {techBreakdown.slice(0, 10).map((tech, idx) => {
                  const compliance = tech.total > 0 ? Math.round((tech.met / tech.total) * 100) : 0;
                  const color = getComplianceColor(compliance);
                  return (
                    <Tr key={idx} _hover={{ bg: tableRowHover }}>
                      <Td>
                        <HStack spacing={2}>
                          <Icon as={MdPerson} color={mutedColor} boxSize={4} />
                          <Text fontSize="sm" fontWeight="500">{tech.technician_name || tech.name || `Tech #${idx + 1}`}</Text>
                        </HStack>
                      </Td>
                      <Td isNumeric>{tech.total ?? 0}</Td>
                      <Td isNumeric><Text color="green.500" fontWeight="600">{tech.met ?? 0}</Text></Td>
                      <Td isNumeric><Text color={tech.breached > 0 ? 'red.500' : mutedColor} fontWeight="600">{tech.breached ?? 0}</Text></Td>
                      <Td isNumeric>
                        <Badge colorScheme={color} fontSize="xs">{compliance}%</Badge>
                      </Td>
                      <Td isNumeric>
                        <Text fontSize="sm" color={mutedColor}>{formatDuration(tech.avg_resolution_minutes)}</Text>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ====== AT-RISK & BREACHED DETAIL TABLE ====== */}
      <Box
        id="sla-detail-table"
        p={5}
        bg={cardBg}
        borderRadius="xl"
        borderWidth="1px"
        borderColor={borderColor}
        boxShadow="sm"
      >
        {/* Toolbar */}
        <Flex justify="space-between" align="center" mb={4} flexWrap="wrap" gap={3}>
          <Heading size="sm" color={headingColor}>Work Order Detail</Heading>
          <HStack spacing={2} flexWrap="wrap">
            {/* Search */}
            <InputGroup size="sm" w={{ base: 'full', md: '220px' }}>
              <InputLeftElement pointerEvents="none">
                <Icon as={MdSearch} color={mutedColor} />
              </InputLeftElement>
              <Input
                placeholder="Search ID, title, tech..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                borderRadius="lg"
              />
            </InputGroup>

            {/* Filters toggle */}
            <Tooltip label="Toggle filters" hasArrow>
              <IconButton
                icon={<Icon as={showFilters ? MdExpandLess : MdFilterList} />}
                size="sm"
                variant={showFilters ? 'solid' : 'outline'}
                colorScheme={showFilters ? 'cyan' : 'gray'}
                onClick={() => setShowFilters(f => !f)}
                aria-label="Filters"
                borderRadius="lg"
              />
            </Tooltip>

            {/* Export */}
            <Button size="sm" leftIcon={<Icon as={MdDownload} />} variant="outline" onClick={handleExport} borderRadius="lg">
              Export
            </Button>
          </HStack>
        </Flex>

        {/* Filter bar */}
        <Collapse in={showFilters} animateOpacity>
          <Flex gap={3} mb={4} p={3} bg={bgColor} borderRadius="lg" flexWrap="wrap">
            <FormControl w="auto">
              <FormLabel fontSize="xs" mb={1}>Priority</FormLabel>
              <Select size="sm" value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} borderRadius="lg" w="140px">
                <option value="all">All Priorities</option>
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </Select>
            </FormControl>
            <FormControl w="auto">
              <FormLabel fontSize="xs" mb={1}>Sort by</FormLabel>
              <Select size="sm" value={sortField} onChange={e => setSortField(e.target.value)} borderRadius="lg" w="180px">
                <option value="time_remaining_minutes">Time Remaining</option>
                <option value="priority">Priority</option>
                <option value="work_order_id">WO ID</option>
                <option value="assigned_to">Technician</option>
              </Select>
            </FormControl>
            <FormControl w="auto" alignSelf="flex-end">
              <Button size="sm" variant="outline" onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')} borderRadius="lg">
                {sortDir === 'asc' ? '↑ Asc' : '↓ Desc'}
              </Button>
            </FormControl>
          </Flex>
        </Collapse>

        {/* Tabs: At Risk / Breached */}
        <Tabs index={activeDetailTab} onChange={setActiveDetailTab} variant="soft-rounded" colorScheme="cyan" size="sm">
          <TabList mb={4}>
            <Tab>
              <HStack spacing={2}>
                <Icon as={MdWarning} color="orange.400" />
                <Text>At Risk</Text>
                <Badge colorScheme="orange" fontSize="xs">{filteredAtRisk.length}</Badge>
              </HStack>
            </Tab>
            <Tab>
              <HStack spacing={2}>
                <Icon as={MdError} color="red.400" />
                <Text>Breached</Text>
                <Badge colorScheme="red" fontSize="xs">{filteredBreached.length}</Badge>
              </HStack>
            </Tab>
          </TabList>

          <TabPanels>
            {/* At Risk Table */}
            <TabPanel p={0}>
              <TicketTable
                rows={filteredAtRisk}
                sortField={sortField}
                sortDir={sortDir}
                onSort={toggleSort}
                onRowClick={openTicketDetail}
                tableHeadBg={tableHeadBg}
                tableRowHover={tableRowHover}
                mutedColor={mutedColor}
                emptyMessage="No at-risk tickets for this period."
                emptyIcon={MdCheckCircle}
                emptyColor="green.500"
              />
            </TabPanel>

            {/* Breached Table */}
            <TabPanel p={0}>
              <TicketTable
                rows={filteredBreached}
                sortField={sortField}
                sortDir={sortDir}
                onSort={toggleSort}
                onRowClick={openTicketDetail}
                tableHeadBg={tableHeadBg}
                tableRowHover={tableRowHover}
                mutedColor={mutedColor}
                emptyMessage="No breached SLAs for this period. 🎉"
                emptyIcon={MdCheckCircle}
                emptyColor="green.500"
              />
            </TabPanel>
          </TabPanels>
        </Tabs>
      </Box>

      {/* ====== TICKET DETAIL MODAL ====== */}
      <Modal isOpen={isTicketOpen} onClose={onTicketClose} size="lg" isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent bg={cardBg} borderRadius="2xl">
          <ModalHeader>
            <HStack spacing={3}>
              <Icon as={MdAssignment} color="cyan.500" />
              <Box>
                <Text fontSize="md" fontWeight="700">WO #{selectedTicket?.work_order_id}</Text>
                <Text fontSize="xs" color={mutedColor} fontWeight="400">{selectedTicket?.title}</Text>
              </Box>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedTicket && (
              <VStack align="stretch" spacing={4}>
                <SimpleGrid columns={2} spacing={4}>
                  {[
                    { label: 'Priority',       value: <Badge colorScheme={SLA_WINDOWS[selectedTicket.priority]?.color || 'gray'} textTransform="capitalize">{selectedTicket.priority}</Badge> },
                    { label: 'SLA Status',     value: <Badge colorScheme={STATUS_CONFIG[selectedTicket.sla_status]?.color || 'gray'}>{STATUS_CONFIG[selectedTicket.sla_status]?.label || selectedTicket.sla_status}</Badge> },
                    { label: 'Assigned To',    value: selectedTicket.assigned_to || 'Unassigned' },
                    { label: 'Zone',           value: selectedTicket.zone_name || '—' },
                    { label: 'SLA Window',     value: SLA_WINDOWS[selectedTicket.priority]?.label || '—' },
                    { label: 'Time Remaining', value: formatDuration(selectedTicket.time_remaining_minutes) },
                    { label: 'Created',        value: selectedTicket.created_at ? new Date(selectedTicket.created_at).toLocaleString() : '—' },
                    { label: 'Due By',         value: selectedTicket.sla_deadline ? new Date(selectedTicket.sla_deadline).toLocaleString() : '—' },
                  ].map(({ label, value }) => (
                    <Box key={label}>
                      <Text fontSize="xs" color={mutedColor} fontWeight="600" mb={0.5}>{label}</Text>
                      <Text fontSize="sm" fontWeight="500">{value}</Text>
                    </Box>
                  ))}
                </SimpleGrid>
                {selectedTicket.description && (
                  <>
                    <Divider />
                    <Box>
                      <Text fontSize="xs" color={mutedColor} fontWeight="600" mb={1}>Description</Text>
                      <Text fontSize="sm">{selectedTicket.description}</Text>
                    </Box>
                  </>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter gap={2}>
            <Button size="sm" variant="outline" onClick={onTicketClose}>Close</Button>
            <Button
              size="sm"
              colorScheme="cyan"
              leftIcon={<Icon as={MdOpenInNew} />}
              onClick={() => window.open(`/work-orders/${selectedTicket?.work_order_id}`, '_blank')}
            >
              Open Work Order
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

// ============================================================================
// EXTRACTED SUB-COMPONENT — Ticket Table (used for both at-risk & breached)
// ============================================================================
const TicketTable = ({
  rows, sortField, sortDir, onSort, onRowClick,
  tableHeadBg, tableRowHover, mutedColor,
  emptyMessage, emptyIcon, emptyColor,
}) => {
  // FIX: Move hooks to the top level of this sub-component
  const headingColor = useColorModeValue('gray.800', 'white');
  const urgentBg = useColorModeValue('red.50', 'red.900');

  if (rows.length === 0) {
    return (
      <Center py={12}>
        <VStack spacing={3}>
          <Icon as={emptyIcon} color={emptyColor} boxSize={10} />
          <Text color={mutedColor} fontSize="sm">{emptyMessage}</Text>
        </VStack>
      </Center>
    );
  }

  const SortHeader = ({ field, children }) => (
    <Th
      cursor="pointer"
      onClick={() => onSort(field)}
      _hover={{ color: 'cyan.500' }}
      userSelect="none"
    >
      <HStack spacing={1}>
        <Text>{children}</Text>
        {sortField === field && <Text fontSize="10px">{sortDir === 'asc' ? '▲' : '▼'}</Text>}
      </HStack>
    </Th>
  );

  return (
    <TableContainer>
      <Table size="sm" variant="simple">
        <Thead bg={tableHeadBg}>
          <Tr>
            <SortHeader field="work_order_id">WO #</SortHeader>
            <Th>Title</Th>
            <SortHeader field="priority">Priority</SortHeader>
            <SortHeader field="assigned_to">Assigned To</SortHeader>
            <Th>Zone</Th>
            <SortHeader field="time_remaining_minutes">Time Remaining</SortHeader>
            <Th>SLA Window</Th>
            <Th>Status</Th>
          </Tr>
        </Thead>
        <Tbody>
          {rows.map((ticket, idx) => {
            const cfg        = STATUS_CONFIG[ticket.sla_status] || STATUS_CONFIG.pending;
            const slaWindow  = SLA_WINDOWS[ticket.priority];
            const isUrgent   = (ticket.time_remaining_minutes ?? 999) < 60;
            return (
              <Tr
                key={ticket.work_order_id ?? idx}
                _hover={{ bg: tableRowHover, cursor: 'pointer' }}
                onClick={() => onRowClick(ticket)}
                bg={isUrgent ? urgentBg : undefined}
              >
                <Td>
                  <Text fontWeight="700" fontSize="sm" color="cyan.500">
                    #{ticket.work_order_id}
                  </Text>
                </Td>
                <Td maxW="200px">
                  <Tooltip label={ticket.title} hasArrow>
                    <Text fontSize="sm" noOfLines={1}>{ticket.title || '—'}</Text>
                  </Tooltip>
                </Td>
                <Td>
                  <Badge colorScheme={slaWindow?.color || 'gray'} fontSize="xs" textTransform="capitalize">
                    {ticket.priority}
                  </Badge>
                </Td>
                <Td>
                  <HStack spacing={1}>
                    <Icon as={MdPerson} color={mutedColor} boxSize={3.5} />
                    <Text fontSize="sm">{ticket.assigned_to || 'Unassigned'}</Text>
                  </HStack>
                </Td>
                <Td>
                  <Text fontSize="sm" color={mutedColor}>{ticket.zone_name || '—'}</Text>
                </Td>
                <Td>
                  <Text
                    fontSize="sm"
                    fontWeight="700"
                    color={isUrgent ? 'red.500' : (ticket.time_remaining_minutes < 180 ? 'orange.500' : 'green.500')}
                  >
                    {formatDuration(ticket.time_remaining_minutes)}
                  </Text>
                </Td>
                <Td>
                  <Tag size="sm" colorScheme={slaWindow?.color || 'gray'} borderRadius="full">
                    <TagLeftIcon as={MdTimer} boxSize={3} />
                    <TagLabel fontSize="xs">{slaWindow?.label || '—'}</TagLabel>
                  </Tag>
                </Td>
                <Td>
                  <Tag size="sm" colorScheme={cfg.color} borderRadius="full" variant="subtle">
                    <TagLeftIcon as={cfg.icon} boxSize={3} />
                    <TagLabel fontSize="xs">{cfg.label}</TagLabel>
                  </Tag>
                </Td>
              </Tr>
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

export default SlaComplianceDashboard;